import axios from "axios";

// Configure axios instance for refund service
const api = axios.create({
  baseURL: import.meta.env.VITE_RIDER_API_URL || "http://localhost:8000/api",
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("authToken");

  if (!token) {
    console.warn(
      "No auth token found in localStorage when making refund API request"
    );
  } else {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem("authToken");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export interface RefundHistory {
  id: string;
  riderId: string;
  riderPublicId: string | null;
  amount: number;
  transactionDate: string;
  utrNumber: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  rider: {
    id: string;
    publicRiderId: string | null;
    name: string | null;
    phone: string;
    city: string | null;
  };
}

export interface RiderWallet {
  rider: {
    id: string;
    publicRiderId: string | null;
    name: string | null;
    phone: string;
  };
  wallet: {
    id: string;
    riderId: string;
    publicRiderId: string | null;
    totalSd: number;
    pendingSd: number;
    fixedDeposit: number | null;
    createdAt: string;
    updatedAt: string;
  };
}

export interface CreateRefundRequest {
  riderId: string;
  amount: number;
  transactionDate: string;
  utrNumber: string;
  fullRefund?: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

class RefundService {
  /**
   * Get all refund history
   */
  async getRefundHistory(): Promise<RefundHistory[]> {
    try {
      const response = await api.get<ApiResponse<RefundHistory[]>>("/v1/refund/history");
      return response.data.data;
    } catch (error) {
      console.error("Error fetching refund history:", error);
      throw error;
    }
  }

  /**
   * Get rider wallet by public rider ID
   */
  async getRiderWalletByPublicId(riderPublicId: string): Promise<RiderWallet> {
    try {
      const response = await api.get<ApiResponse<RiderWallet>>(`/v1/refund/riders/${riderPublicId}/wallet`);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching rider wallet:", error);
      throw error;
    }
  }

  /**
   * Create a new refund
   */
  async createRefund(refundData: CreateRefundRequest): Promise<RefundHistory> {
    try {
      const response = await api.post<ApiResponse<RefundHistory>>("/v1/refund", refundData);
      return response.data.data;
    } catch (error) {
      console.error("Error creating refund:", error);
      throw error;
    }
  }
}

export default new RefundService();